import * as React from "react";
import { useState } from "react";

/**
 * O `useId` quando ele existe, um contador estável quando não.
 *
 * O pacote declara `react: "^17.0.2 || ^18 || ^19"` porque o molde de aplicação
 * da casa ainda tem projetos em React 17, e `useId` só chegou no 18. Em todo o
 * escopo publicado este é o único impedimento — vale um shim de dez linhas em
 * vez de cortar uma major inteira de consumidores.
 *
 * A escolha acontece **no carregamento do módulo**, não dentro do componente:
 * decidir a cada render seria chamada condicional de hook, e o React conta os
 * hooks pela ordem.
 *
 * O caminho do contador não é seguro para hidratação em SSR — servidor e
 * cliente contam separado e divergem. Isso é aceitável aqui e não por
 * descuido: ele só roda no React 17, que não tem RSC, e o pacote inteiro é
 * `"use client"`. Quem estiver no 18 ou 19 recebe o `useId` de verdade, que é
 * seguro.
 */

let counter = 0;

function useFallbackId(): string {
  const [id] = useState(() => `ds2-${++counter}`);
  return id;
}

/* O acesso passa por uma variável de propósito. `React.useId` direto num
   namespace import faz o esbuild converter tudo num import **nomeado** de
   `useId` — que no React 17 não existe, e quebra na resolução do módulo, antes
   de qualquer checagem em runtime: "Named export 'useId' not found". */
const ReactRuntime = React as unknown as Record<string, unknown>;
const nativeUseId = ReactRuntime.useId as (() => string) | undefined;

export const useStableId: () => string =
  typeof nativeUseId === "function" ? nativeUseId : useFallbackId;
